// Breakdown-card derivation + tail orchestration shared by BOTH refine
// surfaces (focus RefineFlow and the IssuePage hub), so the two never drift.
//
//   - Derives THIS issue's `breakdown_card` timeline entry (iid-filtered —
//     the PM session is per PROJECT, other issues' cards ride the same
//     timeline) and latches it across the reconnect replay wipe
//     (useLatchedEntry, keyed sessionId:iid:resetEpoch — the same recipe as
//     the pinned readiness card).
//   - Scopes the transient side-channel states (sizing / unavailable /
//     progress / result) to this issue.
//   - Optionally auto-kicks the plan+sizing tail at the FIRST allGreen
//     (IssuePage has no plan phase of its own; RefineFlow already kicks and
//     passes `autoKick: false`). One-shot per (session, iid, resetEpoch) via
//     the remount-safe store, so a reconnect can't re-fire it.

import { useCallback, useEffect, useMemo, useRef } from "react";

import type { TimelineEntry, UseWsSession } from "../chat/useWsSession";
import { useLatchedEntry } from "../chat/useLatchedEntry";
import type { BreakdownOpPayload } from "../lib/ws";

export type BreakdownEntry = Extract<TimelineEntry, { kind: "breakdown_card" }>;

/** Coarse card phase the hosts render on. `review` requires a live token. */
export type BreakdownPhase =
  | "none"
  | "review"
  | "creating"
  | "done"
  | "failed";

export interface UseBreakdownCard {
  /** Latched proposal entry for this issue, or null. */
  readonly entry: BreakdownEntry | null;
  readonly phase: BreakdownPhase;
  /** Sizing outcome for this issue (null until the tail ran). */
  readonly sizing: Extract<UseWsSession["sizingResult"], { kind: "ready" }> | null;
  readonly sizingUnavailable: boolean;
  /** A sizing whose `criteriaCount` no longer matches the card's current
   *  criteria (the user edited/added acceptance criteria after it ran) — it's
   *  stale, so `sizing` reads null and the host should re-size. */
  readonly sizingStale: boolean;
  /** Breakdown proposal-turn degrade for this issue. */
  readonly breakdownUnavailable: boolean;
  readonly progress: UseWsSession["breakdownProgress"];
  readonly result: UseWsSession["breakdownResult"];
  /** Ask the server for the breakdown proposal turn. */
  readonly requestBreakdown: (force?: boolean) => boolean;
  /** Confirm: one action — parent apply + children creation. */
  readonly acceptBreakdown: (acceptToken: string) => boolean;
  /**
   * Send a typed adjustment. Reads `childrenHash` off the CURRENT entry, so an
   * op composed against a card the server has already superseded is refused
   * (`breakdown_op_stale`) instead of landing on a different piece.
   *
   * Returns false when the entry carries no hash at all — a proposal made
   * before typed ops existed. The card then keeps the prose lane rather than
   * sending ops nothing can address.
   */
  readonly sendOp: (op: BreakdownOpPayload, groundingText?: string) => boolean;
  /** True when this proposal is addressable by typed ops (every child has a
   *  stable id AND the card carries its hash). */
  readonly opsAvailable: boolean;
  /** Outcome of the last typed op for this issue. */
  readonly opResult: UseWsSession["breakdownOpResult"];
}

export const useBreakdownCard = (
  ws: UseWsSession,
  sessionId: string | null,
  issueIid: number | null,
  opts?: {
    readonly autoKickPlan?: boolean;
    readonly allGreen?: boolean;
    /** The card's CURRENT acceptance-criteria count. When it disagrees with the
     *  sizing's `criteriaCount`, the sizing is stale (criteria were edited). */
    readonly currentCriteriaCount?: number;
  },
): UseBreakdownCard => {
  const latchKey = `${sessionId ?? ""}:${issueIid ?? ""}:${ws.resetEpoch}`;

  const derived = useMemo<BreakdownEntry | null>(() => {
    if (issueIid === null) return null;
    for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
      const entry = ws.timeline[i];
      if (entry?.kind === "breakdown_card" && entry.issueIid === issueIid) {
        return entry;
      }
    }
    return null;
  }, [ws.timeline, issueIid]);
  const entry = useLatchedEntry(derived, latchKey);

  const sizingRaw =
    ws.sizingResult?.kind === "ready" && ws.sizingResult.issueIid === issueIid
      ? ws.sizingResult
      : null;
  // Stale once the card's criteria count moves away from what was sized (only
  // meaningful when the host tells us the current count and the card is green).
  const sizingStale =
    sizingRaw !== null &&
    opts?.currentCriteriaCount !== undefined &&
    opts.currentCriteriaCount >= 1 &&
    sizingRaw.criteriaCount !== opts.currentCriteriaCount;
  const sizing = sizingStale ? null : sizingRaw;
  const sizingUnavailable =
    ws.sizingResult?.kind === "unavailable" &&
    ws.sizingResult.issueIid === issueIid;
  const breakdownUnavailable =
    ws.breakdownUnavailable !== null &&
    ws.breakdownUnavailable.issueIid === issueIid;
  const progress =
    ws.breakdownProgress !== null && ws.breakdownProgress.issueIid === issueIid
      ? ws.breakdownProgress
      : null;
  const result =
    ws.breakdownResult !== null && ws.breakdownResult.issueIid === issueIid
      ? ws.breakdownResult
      : null;

  // Auto-kick the plan+sizing tail once the card first goes allGreen
  // (IssuePage host — no plan phase of its own). One-shot PER LATCH KEY via a
  // ref-held set: a keyed useState would read its key only in the initializer,
  // so an in-place key change (issue navigation on the same mounted page, a
  // resetEpoch bump) would carry a stale latch and silently skip the next
  // issue's sizing. Skipped entirely when a sizing outcome for this issue is
  // already known (the attach re-emit restores it from the durable CardState)
  // — no redundant sandbox turn on an already-sized card.
  const kickedKeysRef = useRef<Set<string>>(new Set());
  const sizingKnown = sizing !== null || sizingUnavailable;
  const { sendReadinessPlanRequest } = ws;
  // The one-shot key folds in the current criteria count: when the criteria
  // change (e.g. the piece elicitation turns one criterion into several) the
  // prior sizing goes stale (`sizing` reads null → `sizingKnown` false), and the
  // NEW key isn't in the set, so the tail re-fires EXACTLY once for the new
  // criteria — no re-kick loop (the fresh result lands under the same key).
  const kickKey = `${latchKey}:${opts?.currentCriteriaCount ?? 0}`;
  useEffect(() => {
    if (opts?.autoKickPlan !== true) return;
    if (issueIid === null || opts.allGreen !== true || sizingKnown) return;
    // A live split proposal means the user is mid-review. Parking a must-have
    // changes the criteria count, which makes the prior sizing stale, which
    // used to re-fire a BILLED plan+sizing sandbox turn under the card the user
    // is still working in — and froze it behind `agentBusy` for the duration.
    // The server rebases the sizing map on a park, so there is nothing to
    // re-derive; the kick exists for a card that never had a tail at all.
    if (entry !== null && !entry.noSplit) return;
    if (kickedKeysRef.current.has(kickKey)) return;
    if (sendReadinessPlanRequest(issueIid)) {
      kickedKeysRef.current.add(kickKey);
    }
  }, [
    opts?.autoKickPlan,
    opts?.allGreen,
    issueIid,
    kickKey,
    sizingKnown,
    entry,
    sendReadinessPlanRequest,
  ]);

  const requestBreakdown = useCallback(
    (force?: boolean): boolean =>
      issueIid === null
        ? false
        : ws.sendReadinessBreakdownRequest(issueIid, force),
    [ws, issueIid],
  );
  const acceptBreakdown = useCallback(
    (acceptToken: string): boolean =>
      issueIid === null || entry?.breakdownToken === undefined
        ? false
        : ws.sendBreakdownAccept(issueIid, acceptToken, entry.breakdownToken),
    [ws, issueIid, entry],
  );

  // A card is op-addressable only when the server minted stable ids for EVERY
  // child and sent the hash those ids belong to. Either half missing means this
  // proposal predates typed ops (or came from an older bridge image), and the
  // card must fall back to the prose lane rather than offering buttons whose
  // every press would deny.
  const opsAvailable =
    entry !== null &&
    entry.childrenHash !== undefined &&
    entry.children.length > 0 &&
    entry.children.every((c) => c.childId !== undefined);

  const sendOp = useCallback(
    (op: BreakdownOpPayload, groundingText?: string): boolean => {
      const hash = entry?.childrenHash;
      if (issueIid === null || hash === undefined) return false;
      return ws.sendBreakdownOp(issueIid, hash, op, groundingText);
    },
    [ws, issueIid, entry],
  );

  const opResult =
    ws.breakdownOpResult !== null && ws.breakdownOpResult.issueIid === issueIid
      ? ws.breakdownOpResult
      : null;

  const phase: BreakdownPhase =
    result !== null
      ? result.ok
        ? "done"
        : "failed"
      : progress !== null
        ? "creating"
        : entry !== null && !entry.noSplit
          ? "review"
          : "none";

  return {
    entry,
    phase,
    sizing,
    sizingUnavailable,
    sizingStale,
    breakdownUnavailable,
    progress,
    result,
    requestBreakdown,
    acceptBreakdown,
    sendOp,
    opsAvailable,
    opResult,
  };
};
